/** @format */
import { Thumbnail } from '@mpb-com/feather/components/atoms/thumbnail';
import { TFunction } from 'i18next';
import React, { Fragment, ReactNode } from 'react';

import {
    IconButton,
    IconButtonFill, // eslint-disable-next-line boundaries/element-types -- This should be using the icon from feather
} from '@toucan/client/shared/components/icon-button';

import { Dot, DotColor } from './components/dot';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum PaginationVariant {
    NUMBERED = 'numbered',
    THUMBNAIL = 'thumbnail',
    DOT = 'dot',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface PaginationItemProps {
    href?: string;
    image?: ReactNode;
    text?: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
interface WithPageNum {
    pageNum: number;
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type PaginationItemPropsWithPageNum = PaginationItemProps & WithPageNum;

/**
 * Clamps a value between a minimum and maximum
 *
 * @format
 * @param {number} value - The value to clamp
 * @returns {number} - The clamped value
 */

export function clamp(value: number, min: number, max: number) {
    return Math.max(Math.min(max, value), min);
}

/**
 * Clamps a value between 0 and the number of pages available
 *
 * @param {number} value - The value to clamp between 0 and the number of pagination items.
 * @param {number[]} numItems The number of items
 * @returns {number} The clamped value
 */
export function clampToPaginationRange(value: number, numItems: number) {
    return clamp(value, 0, numItems);
}

/**
 * Returns an IconButton component to be displayed in the pagination
 *
 * @param {PaginationItemPropsWithPageNum} item - Parameters that will populate the IconButton component.
 * @param {number} currentItem the current item index
 * @param {function} onClick A function to be run when IconButton is clicked
 * @param {TFunction} t the i18Next translate function
 * @param {function} itemLabel A function which returns the label for a given item index
 * @returns {ReactNode} - The IconButton component
 */
export function renderButton(
    item: PaginationItemPropsWithPageNum,
    currentItem: number,
    onClick: (event: React.MouseEvent<HTMLElement>) => void,
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    itemLabel?: (index: number) => string
): ReactNode {
    const isDisabled = currentItem === item.pageNum;
    const fillType =
        currentItem === item.pageNum
            ? IconButtonFill.SOLID
            : IconButtonFill.HOLLOW;

    return (
        <IconButton
            href={item.href}
            border={false}
            label={
                itemLabel // eslint-disable-line no-nested-ternary -- Legacy code: do not use as an example.
                    ? itemLabel(item.pageNum)
                    : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Legacy code: do not use as an example.
                      item.text
                      ? item.text
                      : t('Go to page {{pageNumber}}', {
                            pageNumber: item.pageNum + 1,
                        })
            }
            fill={fillType}
            disabled={isDisabled}
            onClick={onClick}
            tabIndex={-1}
            aria-current={item.pageNum === currentItem}
        >
            {item.text}
        </IconButton>
    );
}

/**
 * Returns a Thumbnail component to be displayed in the pagination
 *
 * @param {PaginationItemPropsWithPageNum} item - Parameters that will populate the Thumbnail component.
 * @param {number} currentItem the current item index
 * @param {function} onClick - Function to be run when Thumbnail is clicked
 * @returns {ReactNode} - The Thumbnail component
 */
export function renderThumbnail(
    item: PaginationItemPropsWithPageNum,
    currentItem: number,
    onClick: (event: React.MouseEvent<HTMLElement>) => void
): ReactNode {
    const isActive = currentItem === item.pageNum;
    return (
        <Thumbnail onClick={onClick} className={isActive ? 'active' : ''}>
            {item.image}
        </Thumbnail>
    );
}

/**
 * Returns a Dot component to be displayed in the pagination
 *
 * @param {PaginationItemPropsWithPageNum} item - Parameters that will populate the Dot component.
 * @param {number} currentItem the current item index
 * @param {function} onClick - A function to be run when Dot is clicked
 * @param {TFunction} t the i18Next translate function
 * @param {function} itemLabel A function which returns the label for a given item index
 * @returns {ReactNode} - The Dot component
 */
export function renderDot(
    item: PaginationItemPropsWithPageNum,
    currentItem: number,
    onClick: (event: React.MouseEvent<HTMLElement>) => void,
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    itemLabel?: (index: number) => string
): ReactNode {
    const isActive = currentItem === item.pageNum;
    return (
        <Dot
            disabled={isActive}
            selected={isActive}
            onClick={onClick}
            dotColor={DotColor.INVERTED}
            tabIndex={-1}
            aria-label={
                itemLabel // eslint-disable-line no-nested-ternary -- Legacy code: do not use as an example.
                    ? itemLabel(item.pageNum)
                    : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Legacy code: do not use as an example.
                      item.text
                      ? item.text
                      : t('Go to page {{pageNumber}}', {
                            pageNumber: item.pageNum + 1,
                        })
            }
        />
    );
}

/**
 * Returns an element of given variant to be displayed in the pagination
 *
 * @param {PaginationVariant} variant the pagination variant
 * @param {PaginationItemPropsWithPageNum} item Parameters that will populate the component
 * @param {number} currentItem the current item
 * @param {TFunction} t the i18Next translate function
 * @param {function} itemLabel function to get the label for a given index
 * @param {function} onPage handler for when a page is selected
 * @returns ReactNode - A component of the given type
 */
export function getElement(
    variant: PaginationVariant,
    item: PaginationItemPropsWithPageNum,
    currentItem: number,
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    itemLabel?: (index: number) => string,
    onPage?: (index: number) => void
): ReactNode {
    switch (variant) {
        case PaginationVariant.NUMBERED:
            return renderButton(
                item,
                currentItem,
                (event) => {
                    event.preventDefault();
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
                    onPage && onPage(item.pageNum);
                },
                t,
                itemLabel
            );
        case PaginationVariant.THUMBNAIL:
            return renderThumbnail(item, currentItem, (event) => {
                event.preventDefault();
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
                onPage && onPage(item.pageNum);
            });
        default:
        case PaginationVariant.DOT:
            return renderDot(
                item,
                currentItem,
                (event) => {
                    event.preventDefault();
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
                    onPage && onPage(item.pageNum);
                },
                t,
                itemLabel
            );
    }
}

/**
 * Get an array of pagination item elements from the given array of items
 *
 * @param {PaginationVariant} variant the pagination variant
 * @param {PaginationItemPropsWithPageNum[]} items - The array of items that
 * will be used to generate the pagination elements
 * @param {number} numItems the total number of pages
 * @param {number} currentItem the current item
 * @param {TFunction} t the i18Next translate function
 * @param {function} itemLabel function to get the label for a given index
 * @param {function} onPage handler for when a page is selected
 * @returns {ReactNode} - The elements
 */
export function getElements(
    variant: PaginationVariant,
    items: PaginationItemPropsWithPageNum[],
    numItems: number,
    currentItem: number,
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    showFirstLastButtons: boolean,
    itemLabel?: (index: number) => string,
    onPage?: (index: number) => void
): ReactNode {
    return items.map(
        (item: PaginationItemPropsWithPageNum, i: number, pages) => {
            const { pageNum } = item;
            const prevPageNum = pages[i - 1]?.pageNum;
            const nextPageNum = pages[i + 1]?.pageNum;
            const isFirstItem = i === 0;
            const isLastItem = i === pages.length - 1;

            // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
            const showNextDots =
                showFirstLastButtons &&
                isFirstItem &&
                nextPageNum !== undefined &&
                pageNum + 1 !== nextPageNum;

            // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
            const showPrevDots =
                showFirstLastButtons &&
                isLastItem &&
                prevPageNum !== undefined &&
                pageNum - 1 !== prevPageNum;

            return (
                <Fragment key={pageNum}>
                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {showPrevDots && (
                        <li>
                            <span>…</span>
                        </li>
                    )}
                    <li>
                        {getElement(
                            variant,
                            item,
                            currentItem,
                            t,
                            itemLabel,
                            onPage
                        )}
                    </li>
                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {showNextDots && (
                        <li>
                            <span>…</span>
                        </li>
                    )}
                </Fragment>
            );
        }
    );
}

/**
 * Filters the given items to those that should currently be displayed.
 *
 * @param {PaginationItemPropsWithPageNum[]} items The items
 * @param {number} currentItem the current item
 * @param showFirstLastButtons true if the first and last buttons should be shown
 * @param {number} itemLimit The number of items to show at a time
 * @returns {PaginationItemPropsWithPageNum[]} - A filtered list of items
 */
export function itemsInRange(
    items: PaginationItemPropsWithPageNum[],
    currentItem: number,
    showFirstLastButtons: boolean,
    itemLimit: number = items.length
): PaginationItemPropsWithPageNum[] {
    const range = Math.floor(itemLimit / 2);

    // There aren't enough items in the range so we display all items
    if (itemLimit >= items.length) {
        return items;
    }

    let pages: PaginationItemPropsWithPageNum[] = [];

    if (currentItem < range) {
        // Handle items to show when we are at the start of the pagination
        pages = items.slice(0, itemLimit);
    } else if (items.length - itemLimit + range < currentItem) {
        // Handle items to show when are are the end of the pagination
        pages = items.slice(items.length - itemLimit, items.length);
    } else {
        const min = clampToPaginationRange(currentItem - range, items.length);
        const max = clampToPaginationRange(min + itemLimit, items.length);
        pages = items.slice(min, max);
    }

    if (showFirstLastButtons) {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        const showingFirstPage = pages[0].pageNum === 0;
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        const showingLastPage =
            pages[pages.length - 1].pageNum === items.length - 1;

        if (!showingFirstPage) {
            pages[0] = items[0];
        }

        if (!showingLastPage) {
            pages[pages.length - 1] = items[items.length - 1];
        }
    }

    return pages;
}
